// src/components/ProdMgrs/hooks/useProdMgrsRepOptions.js
// =============================================================================
// BLOCK PMGR.HOOK.REPOPTS.0 — Load distinct REPID values for selected year
// Purpose:
//   - Fetch unique REPID values from pmgr_comm for selectedYear
//   - Used to populate ProdMgrs rep dropdown
//
// Contract:
//   returns {
//     options: string[],
//     loading: boolean,
//     error: string | null
//   }
//
// Notes:
//   - Keeps ESLint `consistent-return` satisfied by ALWAYS returning a cleanup
//     function from useEffect.
// =============================================================================

import React from 'react';

import axios from '../../../axiosClient.js';

export default function useProdMgrsRepOptions(selectedYear) {
  const [options, setOptions] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    // -------------------------------------------------------------------------
    // BLOCK PMGR.HOOK.REPOPTS.1 — Early validation
    // -------------------------------------------------------------------------
    const year = Number(selectedYear);

    let cancelled = false;

    if (!Number.isFinite(year)) {
      setOptions([]);
      setError('Invalid year');

      // IMPORTANT: return cleanup to satisfy consistent-return
      return () => {
        cancelled = true;
      };
    }

    // -------------------------------------------------------------------------
    // BLOCK PMGR.HOOK.REPOPTS.2 — Fetch repids for year
    // -------------------------------------------------------------------------
    setLoading(true);
    setError(null);

    axios
      .get('/api/prod-mgrs/repids', { params: { year } })
      .then((res) => {
        if (cancelled) {
          return;
        }

        const arr = Array.isArray(res?.data) ? res.data : [];

        // Defensive cleanup + uniqueness
        const unique = Array.from(
          new Set(arr.map((v) => String(v ?? '').trim()).filter(Boolean))
        ).sort((a, b) => a.localeCompare(b));

        setOptions(unique);
      })
      .catch((e) => {
        if (cancelled) {
          return;
        }

        setOptions([]);
        setError(e?.message || 'Failed to load reps');
      })
      .finally(() => {
        if (cancelled) {
          return;
        }
        setLoading(false);
      });

    // -------------------------------------------------------------------------
    // BLOCK PMGR.HOOK.REPOPTS.3 — Cleanup
    // -------------------------------------------------------------------------
    return () => {
      cancelled = true;
    };
  }, [selectedYear]);

  return { options, loading, error };
}
